import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "@inertiajs/react";
import { type FormEvent, useEffect } from "react";
import TextArea from "@/components/forms/TextArea";

type Props = {
  domainId: string;
  issueId: string;
  open: boolean;
  onClose: () => void;
};

export default function ReportFalsePositiveDialog({ domainId, issueId, open, onClose }: Props) {
  const { data, setData, post, processing, errors, reset, clearErrors, transform } = useForm({ reason: "" });
  transform((form) => ({ false_positive_report: form }));

  useEffect(() => {
    if (!open || processing) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, processing, onClose]);

  if (!open) {
    return null;
  }

  function handleClose() {
    if (processing) {
      return;
    }

    reset();
    clearErrors();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    post(`/domains/${domainId}/issues/${issueId}/false_positive_report`, {
      preserveScroll: true,
      preserveState: "errors",
      onSuccess: () => {
        reset();
        onClose();
      },
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm"
        onClick={handleClose}
        disabled={processing}
      />
      <form className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg" onSubmit={handleSubmit}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Report false positive</h2>
            <p className="mt-2 text-sm text-gray-600">
              This hides the issue for the customer. Tell us why it is not a real usability problem.
            </p>
          </div>
          <button
            type="button"
            className="cursor-pointer text-gray-400 hover:text-gray-600"
            onClick={handleClose}
            aria-label="Close"
            disabled={processing}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <TextArea
          error={errors.reason}
          label="Why is this a false positive?"
          onChange={(value) => setData("reason", value)}
          required
          value={data.reason}
        />

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" className="btn" onClick={handleClose} disabled={processing}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={processing}>
            {processing ? "Reporting…" : "Report false positive"}
          </button>
        </div>
      </form>
    </div>
  );
}
